<template>
  <div class="mt-2">
    <WarningDisable permission="record" />
    <div class="grow">
      <div class="mb-2 flex items-center justify-between">
        <div class="text-base-content/80 text-xl font-bold">
          Bản ghi gần đây
        </div>
        <select v-model="filters.mode" class="select w-44">
          <option value="">Tất cả</option>
          <option value="auto">Tự động</option>
          <option value="manual">Thủ công</option>
        </select>
      </div>
      <Loading v-if="isLoading && records.length === 0" />
      <div v-else-if="isError" class="alert alert-error">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>Không tải được danh sách bản ghi.</span>
        <button class="btn btn-sm" @click="refetch()">Thử lại</button>
      </div>
      <template v-else-if="totalFiltered > 0">
        <div class="overflow-x-auto">
          <table class="table-pin-rows table-md mb-4 table">
            <thead>
              <tr>
                <th>File</th>
                <th>Thời gian tạo</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="bg-base-200">
              <tr v-for="rec in records" :key="rec.id">
                <td class="font-mono text-sm">
                  {{ rec.path.split("/").pop() || rec.path }}
                </td>
                <td>{{ dayjs(rec.created).format("DD/MM/YYYY HH:mm:ss") }}</td>
                <td>
                  <button
                    class="btn btn-ghost btn-sm"
                    title="Xem"
                    @click="openPreview(rec)"
                  >
                    <i class="fa-solid fa-play"></i>
                  </button>
                  <button
                    class="btn btn-ghost btn-sm"
                    title="Tải xuống"
                    @click="download(rec)"
                  >
                    <i class="fa-solid fa-download"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="isFetchingNextPage" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
        <div v-else-if="hasNextPage" class="flex justify-center py-4">
          <button @click="() => fetchNextPage()" class="btn btn-primary">
            Tải thêm
          </button>
        </div>
      </template>
      <NotFound v-else />
    </div>
    <dialog ref="previewDialog" class="modal">
      <div class="modal-box max-w-3xl">
        <h3 class="text-lg font-bold break-all">
          {{ previewRecord?.path.split("/").pop() }}
        </h3>
        <video
          controls
          preload="metadata"
          class="mt-4 w-full bg-black"
          :src="previewUrl"
        ></video>
        <div class="modal-action">
          <button class="btn" @click="closePreview">Đóng</button>
          <button
            v-if="previewRecord"
            class="btn btn-primary"
            @click="download(previewRecord)"
          >
            <i class="fa-solid fa-download"></i>
            Tải xuống
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { getRecordUrl, type RecordItem } from "@/api/record";
import { downloadFileFromUrl } from "@/composables/useDownload";
import Loading from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import { router } from "@/router/router";
import { useInfiniteScroll } from "@vueuse/core";
import dayjs from "dayjs";
import { computed, onMounted, reactive, ref, watch } from "vue";
import WarningDisable from "../WarningDisable.vue";
import { useRecordsInfiniteQuery } from "./useRecordsInfiniteQuery";

// Reactive filters based on route params
const filters = reactive({
  agent_id: router.currentRoute.value.params.client_id as string,
  size: 20,
  mode: "",
});

// Re-fetch when navigating to another client (component-scoped, auto-disposed)
watch(
  () => router.currentRoute.value.params.client_id,
  (clientId) => {
    if (clientId && clientId !== filters.agent_id) {
      filters.agent_id = clientId as string;
    }
  },
);

const {
  records,
  totalFiltered,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  refetch,
} = useRecordsInfiniteQuery(filters);

const download = (rec: RecordItem) => {
  downloadFileFromUrl(
    getRecordUrl(rec.path),
    rec.path.split("/").pop() || rec.id,
  );
};

const previewDialog = ref<HTMLDialogElement | null>(null);
const previewRecord = ref<RecordItem | null>(null);
const previewUrl = computed(() =>
  previewRecord.value ? getRecordUrl(previewRecord.value.path) : "",
);

const openPreview = (rec: RecordItem) => {
  previewRecord.value = rec;
  previewDialog.value?.showModal();
};

const closePreview = () => {
  previewDialog.value?.close();
  previewRecord.value = null;
};

onMounted(() => {
  useInfiniteScroll(
    document.querySelector("main"),
    () => {
      if (hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { distance: 100 },
  );
});
</script>
